/**
 * InvoiceDocument — an elegant, print-ready, CoLiviq-branded invoice.
 *
 * Rendered inside the preview dialog (View) and also serialized to HTML for the
 * "Send invoice" email. Styling is self-contained (scoped class names + inline
 * style block) so it survives being lifted out of the app into an email body.
 *
 * When `data.locale === "de"` (owner management fees) it renders the German SEV
 * "Rechnung" layout; otherwise it falls back to the generic English layout.
 */
import type { InvoiceData } from "@/lib/invoice";
import { formatCents } from "@/lib/money";
import { isOffsetInvoice } from "@/types/billing";

const BRAND = "#0f766e"; // teal-700, coliviq accent
const BAR = "#3aab9c"; // brighter teal for the table header bar
const INK = "#0f172a"; // slate-900
const MUTED = "#64748b"; // slate-500
const LINE = "#e2e8f0"; // slate-200

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function deLongDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function deEuros(cents: number): string {
  return `${new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((cents ?? 0) / 100)} EUR`;
}

/**
 * `wordmarkSrc` defaults to the app-served asset; the email serializer passes
 * an absolute https URL so the logo resolves in the recipient's inbox.
 */
export function InvoiceDocument({
  data,
  wordmarkSrc = "/coliviq-logo.png",
}: {
  data: InvoiceData;
  /**
   * Lowercase "coliviq" wordmark. Both layouts carry it alone — no house icon,
   * no typeset name. The email serializer passes an absolute https URL so it
   * resolves in the recipient's inbox.
   */
  wordmarkSrc?: string;
}) {
  if (data.locale === "de") {
    return <GermanInvoice data={data} wordmarkSrc={wordmarkSrc} />;
  }
  return <GenericInvoice data={data} wordmarkSrc={wordmarkSrc} />;
}

// ── German SEV management invoice (matches the CoLiviq template) ──────
function GermanInvoice({
  data,
  wordmarkSrc,
}: {
  data: InvoiceData;
  wordmarkSrc: string;
}) {
  const reference = data.reference || data.invoiceNo;
  // An offset invoice has no payment to reference — no wire transfer will ever
  // happen against this document, so a "Verwendungszweck"/"Fällig am" block
  // (and the "Fällig am: —" it would otherwise print, since dueDate is always
  // null here) would contradict the settlement note in `notes` below. The
  // invoice number is already shown in the meta grid above.
  const offset = isOffsetInvoice({ settlement: data.settlement });
  return (
    <div
      className="cl-invoice cl-de"
      style={{ background: "#fff", color: INK }}
    >
      <style>{`
        .cl-de { font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; width: 100%; max-width: 760px; margin: 0 auto; padding: 48px; box-sizing: border-box; font-size: 13px; line-height: 1.55; }
        .cl-de .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
        .cl-de .brand { display: flex; align-items: center; }
        .cl-de .brand img { height: 30px; width: auto; }
        .cl-de .issuer { text-align: right; font-size: 12px; color: ${MUTED}; }
        .cl-de .issuer .n { font-weight: 700; color: ${INK}; }
        .cl-de .rule { height: 1px; background: ${LINE}; margin: 22px 0 18px; }
        .cl-de .addr { display: flex; justify-content: space-between; gap: 32px; }
        .cl-de .sender { font-size: 10px; color: #94a3b8; margin-bottom: 8px; }
        .cl-de .who { font-weight: 600; }
        .cl-de .ln { color: ${MUTED}; }
        .cl-de .meta { font-size: 12px; min-width: 240px; }
        .cl-de .meta .row { display: flex; justify-content: space-between; gap: 16px; padding: 2px 0; }
        .cl-de .meta .k { color: ${MUTED}; }
        .cl-de .meta .v { font-weight: 600; }
        .cl-de h2 { font-size: 18px; font-weight: 700; margin: 26px 0 12px; }
        .cl-de .sal { margin-bottom: 10px; }
        .cl-de .intro { color: #334155; margin-bottom: 22px; }
        .cl-de .bar { display: flex; justify-content: space-between; background: ${BAR}; color: #fff; font-size: 11px; letter-spacing: 0.04em; padding: 8px 12px; border-radius: 3px; }
        .cl-de .item { display: flex; justify-content: space-between; gap: 20px; padding: 12px 2px 16px; border-bottom: 1px solid ${LINE}; }
        .cl-de .item .desc { font-weight: 600; }
        .cl-de .item .sub { color: #6b7682; font-size: 12px; margin-top: 5px; }
        .cl-de .item .amt { white-space: nowrap; }
        .cl-de .totals { width: 260px; margin-left: auto; margin-top: 14px; }
        .cl-de .totals .row { display: flex; justify-content: space-between; padding: 4px 0; color: #334155; }
        .cl-de .totals .row.total { margin-top: 6px; padding-top: 10px; border-top: 2px solid ${INK}; font-weight: 700; font-size: 15px; color: ${INK}; }
        .cl-de .foot { display: flex; gap: 40px; margin-top: 34px; font-size: 12px; }
        .cl-de .foot .col { flex: 1; }
        .cl-de .foot .h { color: ${BRAND}; font-size: 11px; letter-spacing: 0.04em; margin-bottom: 8px; font-weight: 700; }
        .cl-de .foot .row { display: flex; justify-content: space-between; gap: 12px; padding: 2px 0; }
        .cl-de .foot .k { color: #6b7682; }
        .cl-de .foot .v { font-weight: 600; text-align: right; }
        .cl-de .notes { margin-top: 30px; color: ${MUTED}; font-size: 12px; }
        @media print { .cl-de { padding: 24px; } }
      `}</style>

      <div className="head">
        <div className="brand">
          <img src={wordmarkSrc} alt="coliviq" />
        </div>
        <div className="issuer">
          <div className="n">{data.seller.name}</div>
          {data.seller.lines.map((l, i) => (
            <div key={i}>{l}</div>
          ))}
        </div>
      </div>

      <div className="rule" />

      <div className="addr">
        <div>
          <div className="sender">
            {data.seller.name} · {data.seller.lines[0]}
          </div>
          <div className="who">{data.billTo.name}</div>
          {data.billTo.lines.map((l, i) => (
            <div className="ln" key={i}>
              {l}
            </div>
          ))}
        </div>
        <div className="meta">
          {data.meta.map((m, i) => (
            <div className="row" key={i}>
              <span className="k">{m.label}</span>
              <span className="v">{m.value}</span>
            </div>
          ))}
        </div>
      </div>

      <h2>Rechnung</h2>
      {data.salutation && <div className="sal">{data.salutation}</div>}
      {data.intro && <div className="intro">{data.intro}</div>}

      <div className="bar">
        <span>LEISTUNGSBESCHREIBUNG</span>
        <span>BETRAG{data.taxCents > 0 ? " (NETTO)" : ""}</span>
      </div>
      {data.lineItems.map((li, i) => (
        <div className="item" key={i}>
          <div>
            <div className="desc">{li.description}</div>
            {li.detail && <div className="sub">{li.detail}</div>}
            {li.details?.map((d, j) => (
              <div className="sub" key={j}>
                {d}
              </div>
            ))}
          </div>
          <div className="amt">{deEuros(li.amountCents)}</div>
        </div>
      ))}

      <div className="totals">
        {data.taxCents > 0 && (
          <>
            <div className="row">
              <span>Nettobetrag</span>
              <span>{deEuros(data.subtotalCents)}</span>
            </div>
            <div className="row">
              <span>zzgl. MwSt. {data.vatRate ?? 19} %</span>
              <span>{deEuros(data.taxCents)}</span>
            </div>
          </>
        )}
        <div className="row total">
          <span>Gesamtbetrag</span>
          <span>{deEuros(data.totalCents)}</span>
        </div>
      </div>

      {!offset && (data.bank || reference) && (
        <div className="foot">
          {data.bank && (
            <div className="col">
              <div className="h">BANKVERBINDUNG</div>
              <div className="row">
                <span className="k">Empfänger</span>
                <span className="v">{data.bank.recipient}</span>
              </div>
              <div className="row">
                <span className="k">IBAN</span>
                <span className="v">{data.bank.iban}</span>
              </div>
              {data.bank.bic && (
                <div className="row">
                  <span className="k">BIC</span>
                  <span className="v">{data.bank.bic}</span>
                </div>
              )}
              {data.bank.bankName && (
                <div className="row">
                  <span className="k">Bank</span>
                  <span className="v">{data.bank.bankName}</span>
                </div>
              )}
            </div>
          )}
          <div className="col">
            <div className="h">VERWENDUNGSZWECK</div>
            <div className="row">
              <span className="k">Referenz</span>
              <span className="v">{reference}</span>
            </div>
            <div className="row">
              <span className="k">Fällig am</span>
              <span className="v">{deLongDate(data.dueDate)}</span>
            </div>
          </div>
        </div>
      )}

      {data.notes && <div className="notes">{data.notes}</div>}
    </div>
  );
}

// ── Generic English invoice (rent / deposit / saas) ──────────────────
function GenericInvoice({
  data,
  wordmarkSrc,
}: {
  data: InvoiceData;
  wordmarkSrc: string;
}) {
  const fmt = (c: number) => formatCents(c, data.currency);

  return (
    <div className="cl-invoice" style={{ background: "#fff", color: INK }}>
      <style>{`
        .cl-invoice { font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; width: 100%; max-width: 760px; margin: 0 auto; padding: 48px; box-sizing: border-box; font-size: 14px; line-height: 1.5; }
        .cl-invoice h1 { margin: 0; font-size: 30px; letter-spacing: -0.02em; }
        .cl-inv-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; padding-bottom: 28px; border-bottom: 2px solid ${BRAND}; }
        .cl-inv-brand { display: flex; align-items: center; }
        .cl-inv-brand img { height: 32px; width: auto; }
        .cl-inv-tag { text-align: right; }
        .cl-inv-tag .word { font-size: 26px; font-weight: 700; color: ${BRAND}; letter-spacing: 0.04em; }
        .cl-inv-tag .no { margin-top: 4px; font-size: 13px; color: ${MUTED}; }
        .cl-inv-parties { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-top: 28px; }
        .cl-inv-label { text-transform: uppercase; letter-spacing: 0.08em; font-size: 11px; font-weight: 600; color: ${MUTED}; margin-bottom: 6px; }
        .cl-inv-party .who { font-weight: 600; font-size: 15px; }
        .cl-inv-party .ln { color: ${MUTED}; font-size: 13px; }
        .cl-inv-dates { display: flex; gap: 28px; margin-top: 24px; flex-wrap: wrap; }
        .cl-inv-dates .k { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: ${MUTED}; }
        .cl-inv-dates .v { font-weight: 600; }
        .cl-inv-meta { margin-top: 22px; display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px 32px; padding: 16px 20px; background: #f8fafc; border: 1px solid ${LINE}; border-radius: 12px; }
        .cl-inv-meta .row { display: flex; justify-content: space-between; font-size: 13px; }
        .cl-inv-meta .row .k { color: ${MUTED}; }
        .cl-inv-meta .row .v { font-weight: 500; }
        table.cl-inv-items { width: 100%; border-collapse: collapse; margin-top: 28px; }
        table.cl-inv-items thead th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: ${MUTED}; padding: 0 0 10px; border-bottom: 1px solid ${LINE}; }
        table.cl-inv-items thead th.amt { text-align: right; }
        table.cl-inv-items tbody td { padding: 14px 0; border-bottom: 1px solid ${LINE}; vertical-align: top; }
        table.cl-inv-items tbody td.amt { text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; }
        .cl-inv-items .desc { font-weight: 600; }
        .cl-inv-items .sub { color: ${MUTED}; font-size: 12.5px; margin-top: 2px; }
        .cl-inv-totals { margin-top: 18px; margin-left: auto; width: 280px; }
        .cl-inv-totals .row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 14px; }
        .cl-inv-totals .row.total { margin-top: 6px; padding-top: 12px; border-top: 2px solid ${INK}; font-size: 18px; font-weight: 700; }
        .cl-inv-totals .row.total .v { color: ${BRAND}; }
        .cl-inv-notes { margin-top: 36px; padding-top: 18px; border-top: 1px solid ${LINE}; color: ${MUTED}; font-size: 12.5px; }
        @media print { .cl-invoice { padding: 24px; } }
      `}</style>

      <div className="cl-inv-head">
        <div className="cl-inv-brand">
          <img src={wordmarkSrc} alt="coliviq" />
        </div>
        <div className="cl-inv-tag">
          <div className="word">INVOICE</div>
          <div className="no">{data.invoiceNo}</div>
        </div>
      </div>

      <div className="cl-inv-parties">
        <div className="cl-inv-party">
          <div className="cl-inv-label">From</div>
          <div className="who">{data.seller.name}</div>
          {data.seller.lines.map((l, i) => (
            <div className="ln" key={i}>
              {l}
            </div>
          ))}
        </div>
        <div className="cl-inv-party">
          <div className="cl-inv-label">Bill to</div>
          <div className="who">{data.billTo.name}</div>
          {data.billTo.email ? (
            <div className="ln">{data.billTo.email}</div>
          ) : null}
          {data.billTo.lines.map((l, i) => (
            <div className="ln" key={i}>
              {l}
            </div>
          ))}
        </div>
      </div>

      <div className="cl-inv-dates">
        <div>
          <div className="k">Issued</div>
          <div className="v">{fmtDate(data.issueDate)}</div>
        </div>
        <div>
          <div className="k">Due</div>
          <div className="v">{fmtDate(data.dueDate)}</div>
        </div>
        <div>
          <div className="k">Period</div>
          <div className="v">{data.periodLabel}</div>
        </div>
      </div>

      {data.meta.length > 0 && (
        <div className="cl-inv-meta">
          {data.meta.map((m, i) => (
            <div className="row" key={i}>
              <span className="k">{m.label}</span>
              <span className="v">{m.value}</span>
            </div>
          ))}
        </div>
      )}

      <table className="cl-inv-items">
        <thead>
          <tr>
            <th>Description</th>
            <th className="amt">Amount</th>
          </tr>
        </thead>
        <tbody>
          {data.lineItems.map((li, i) => (
            <tr key={i}>
              <td>
                <div className="desc">{li.description}</div>
                {li.detail ? <div className="sub">{li.detail}</div> : null}
              </td>
              <td className="amt">{fmt(li.amountCents)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="cl-inv-totals">
        <div className="row">
          <span>Subtotal</span>
          <span>{fmt(data.subtotalCents)}</span>
        </div>
        {data.taxCents > 0 ? (
          <div className="row">
            <span>VAT</span>
            <span>{fmt(data.taxCents)}</span>
          </div>
        ) : null}
        <div className="row total">
          <span>Total</span>
          <span className="v">{fmt(data.totalCents)}</span>
        </div>
      </div>

      {data.notes ? <div className="cl-inv-notes">{data.notes}</div> : null}
    </div>
  );
}
